import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  Button,
  Flex,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tooltip,
  Typography,
} from 'antd'
import type { TableProps } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import {
  BarChartOutlined,
  EditOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import type { Partner } from '@/data'
import { getPartners } from '@/api/lop/partners/getPartners'
import { usePartnerSearch } from '@/utils/usePartnerSearch'
import { resolveBackendSort } from '@/utils/backendSort'
import { AuthContext } from '@/contexts/AuthContext'
import { hasPermission } from '@/auth/access'

const { Title, Text } = Typography

export const BiddingList: React.FC = () => {
  const navigate = useNavigate()
  const auth = useContext(AuthContext)!

  const canReadBidding = hasPermission(auth.user, 'lop:ui:action:read:bidding')
  const canCreateBidding = hasPermission(
    auth.user,
    'lop:ui:action:create:bidding',
  )
  const canUpdateBidding = hasPermission(
    auth.user,
    'lop:ui:action:update:bidding',
  )

  const [partners, setPartners] = useState<Partner[]>([])
  const [loading, setLoading] = useState(canReadBidding)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  })
  const [sortField, setSortField] = useState('name')
  const [search, setSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null)
  const hasFetched = useRef(false)

  const columnSortKeys = {
    name: 'name',
    affiliate_id: 'affiliate_id',
    bid_rungs_count: 'bid_rungs_count',
    max_bid: 'max_bid',
  } as const

  const fetchPartners = useCallback(
    async (page = 1, sort = sortField, query = search) => {
      try {
        setLoading(true)
        setError(null)
        const filters = [{ key: 'has_ladder', value: '1' }]
        if (query) filters.push({ key: 'partner', value: query })
        const { data, meta } = await getPartners({
          page,
          sort,
          filters,
          include: 'bidRungs',
        })
        setPartners(data)
        setPagination({
          current: meta.current_page,
          pageSize: meta.per_page,
          total: meta.total,
        })
      } catch (err) {
        setError('Failed to load bid ladders')
        console.error(err)
      } finally {
        setLoading(false)
      }
    },
    [sortField, search],
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearch(value)
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        fetchPartners(1, sortField, value)
      }, 300)
    },
    [fetchPartners, sortField],
  )

  useEffect(() => {
    if (hasFetched.current) return
    if (!canReadBidding) return
    hasFetched.current = true
    fetchPartners()
  }, [canReadBidding, fetchPartners])

  const columns: TableProps<Partner>['columns'] = [
    {
      title: 'Partner',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text: string, record) =>
        canUpdateBidding ? (
          <Link to={`/bidding/${record.id}/edit/bids`}>{text}</Link>
        ) : (
          text
        ),
    },
    {
      title: 'Affiliate ID',
      dataIndex: 'affiliate_id',
      key: 'affiliate_id',
      sorter: true,
    },
    {
      title: 'Bids',
      dataIndex: 'bid_rungs_count',
      key: 'bid_rungs_count',
      sorter: true,
      render: (value: number | undefined) => value ?? '—',
    },
    {
      title: 'Top bid',
      dataIndex: 'max_bid',
      key: 'max_bid',
      sorter: true,
      render: (value: number | null | undefined) =>
        value != null
          ? `$${value.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Flex gap="middle">
          {canUpdateBidding ? (
            <Tooltip title="Edit">
              <Link to={`/bidding/${record.id}/edit/bids`}>
                <EditOutlined style={{ fontSize: 16 }} />
              </Link>
            </Tooltip>
          ) : null}
          <Tooltip title="Recent outcomes">
            <BarChartOutlined
              style={{ fontSize: 16, cursor: 'pointer' }}
              onClick={() =>
                navigate(`/reporting/bid-outcomes?partner=${record.id}`)
              }
            />
          </Tooltip>
        </Flex>
      ),
    },
  ]

  if (error) {
    return (
      <div>
        <Title level={4}>Bidding</Title>
        <Text style={{ color: '#ff4d4f' }}>{error}</Text>
        <Button onClick={() => fetchPartners()}>Retry</Button>
      </div>
    )
  }

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Bidding
        </Title>
        <Flex gap="small" align="center">
          <Input
            placeholder="Search by partner name or ID..."
            prefix={<SearchOutlined />}
            allowClear
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            style={{ width: 260 }}
          />
          {canCreateBidding ? (
            <Button type="primary" onClick={() => setPickerOpen(true)}>
              New Ladder
            </Button>
          ) : null}
        </Flex>
      </Flex>
      {canReadBidding ? (
        <Spin spinning={loading}>
          <Table<Partner>
            rowKey="id"
            columns={columns}
            dataSource={partners}
            pagination={pagination}
            onChange={(p, _filters, sorter) => {
              const sort = resolveBackendSort(sorter, columnSortKeys, 'name')
              setSortField(sort)
              fetchPartners(p.current, sort, search)
            }}
          />
        </Spin>
      ) : null}

      <PartnerPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(partnerId) => {
          setPickerOpen(false)
          navigate(`/bidding/${partnerId}/edit/bids`)
        }}
      />
    </div>
  )
}

interface PartnerPickerModalProps {
  open: boolean
  onClose: () => void
  onSelect: (partnerId: number) => void
}

const PartnerPickerModal: React.FC<PartnerPickerModalProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const [selectedId, setSelectedId] = useState<number | undefined>()
  const { options, loading, hasMore, onSearch, loadMore } = usePartnerSearch()

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement
      const nearBottom =
        target.scrollTop + target.clientHeight >= target.scrollHeight - 40
      if (nearBottom) loadMore()
    },
    [loadMore],
  )

  return (
    <Modal
      title="Create Bid Ladder"
      open={open}
      onCancel={() => {
        setSelectedId(undefined)
        onClose()
      }}
      onOk={() => {
        if (selectedId != null) onSelect(selectedId)
      }}
      okText="Open Editor"
      okButtonProps={{ disabled: selectedId == null }}
    >
      <Flex vertical gap="small">
        <Text>Pick a partner to build a bid ladder for:</Text>
        <Select<number>
          showSearch
          allowClear
          value={selectedId}
          onChange={(v) => setSelectedId(v as number | undefined)}
          onSearch={onSearch}
          onPopupScroll={handleScroll}
          placeholder="Search by name or ID..."
          style={{ width: '100%' }}
          loading={loading}
          suffixIcon={<SearchOutlined />}
          filterOption={false}
          options={options}
          notFoundContent={loading ? <Spin size="small" /> : 'No matches'}
          dropdownRender={(menu) => (
            <>
              {menu}
              {loading && options.length > 0 && (
                <Flex justify="center" style={{ padding: 8 }}>
                  <Spin size="small" />
                </Flex>
              )}
              {!hasMore && options.length > 0 && (
                <Flex justify="center" style={{ padding: '4px 0' }}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    All results loaded
                  </Text>
                </Flex>
              )}
            </>
          )}
        />
      </Flex>
    </Modal>
  )
}
